import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Save, RefreshCw, FileText, Scale, Shield, Eye, ExternalLink } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const PAGE_CONFIG = {
  'mentions-legales': {
    icon: FileText,
    title: 'Mentions légales',
    description: 'Informations légales obligatoires (identité, hébergeur, etc.)',
    color: 'text-blue-500',
    previewUrl: '/site/mentions-legales'
  },
  'cgv': {
    icon: Scale,
    title: 'Conditions Générales de Vente',
    description: 'Règles de vente, paiement, livraison, retours',
    color: 'text-amber-500',
    previewUrl: '/site/cgv'
  },
  'confidentialite': {
    icon: Shield,
    title: 'Politique de confidentialité',
    description: 'Traitement des données personnelles (RGPD)',
    color: 'text-green-500',
    previewUrl: '/site/confidentialite'
  },
  'cookies': {
    icon: Shield,
    title: 'Politique des cookies',
    description: 'Utilisation des cookies et traceurs sur le site',
    color: 'text-purple-500',
    previewUrl: '/site/cookies'
  }
};

const CMSPagesPage = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('mentions-legales');
  const [editedPages, setEditedPages] = useState({});

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const res = await api.get('/cms/pages');
      const pagesData = res.data || [];
      setPages(pagesData);
      // Initialize edited pages state
      const edited = {};
      pagesData.forEach(p => {
        edited[p.id] = { ...p };
      });
      setEditedPages(edited);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (pageId) => {
    setSaving(true);
    try {
      await api.put(`/cms/pages/${pageId}`, editedPages[pageId]);
      toast.success('Page enregistrée');
      loadPages();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updatePage = (pageId, field, value) => {
    setEditedPages(prev => ({
      ...prev,
      [pageId]: {
        ...prev[pageId],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Pages Légales</h1>
        <p className="text-slate-500">Gérez vos pages de conformité légale</p>
      </div>

      {/* Page Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(PAGE_CONFIG).map(([pageId, config]) => {
          const page = editedPages[pageId];
          const Icon = config.icon;
          return (
            <Card
              key={pageId}
              className={`cursor-pointer transition-all hover:shadow-lg ${activeTab === pageId ? 'ring-2 ring-cyan-500' : ''}`}
              onClick={() => setActiveTab(pageId)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg bg-slate-100 ${config.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{config.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{config.description}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant={page?.is_active ? 'default' : 'secondary'}>
                        {page?.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {page?.content && (
                        <span className="text-xs text-slate-400">
                          {page.content.length} caractères
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Editor */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {(() => {
                  const Icon = PAGE_CONFIG[activeTab]?.icon || FileText;
                  return <Icon className={`w-5 h-5 ${PAGE_CONFIG[activeTab]?.color}`} />;
                })()}
                {PAGE_CONFIG[activeTab]?.title}
              </CardTitle>
              <CardDescription>{PAGE_CONFIG[activeTab]?.description}</CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="active-toggle" className="text-sm">Active</Label>
                <Switch
                  id="active-toggle"
                  checked={editedPages[activeTab]?.is_active || false}
                  onCheckedChange={(checked) => updatePage(activeTab, 'is_active', checked)}
                />
              </div>
              <Button
                onClick={() => handleSave(activeTab)}
                disabled={saving}
                className="bg-gradient-to-r from-cyan-500 to-blue-600"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Title override */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Titre personnalisé</Label>
              <Input
                value={editedPages[activeTab]?.title || ''}
                onChange={(e) => updatePage(activeTab, 'title', e.target.value)}
                placeholder={PAGE_CONFIG[activeTab]?.title}
              />
            </div>
            <div className="space-y-2">
              <Label>Slug (URL)</Label>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm">/</span>
                <Input
                  value={editedPages[activeTab]?.slug || activeTab}
                  onChange={(e) => updatePage(activeTab, 'slug', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          {/* Content Editor */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Contenu (HTML supporté)</Label>
              <span className="text-xs text-slate-400">
                {(editedPages[activeTab]?.content || '').length} caractères
              </span>
            </div>
            <Textarea
              value={editedPages[activeTab]?.content || ''}
              onChange={(e) => updatePage(activeTab, 'content', e.target.value)}
              placeholder="Contenu de la page (HTML autorisé)..."
              rows={20}
              className="font-mono text-sm"
            />
          </div>

          {/* Help */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium text-slate-700 mb-2">Aide à la rédaction</h4>
            <p className="text-sm text-slate-500 mb-3">
              Vous pouvez utiliser du HTML pour formater votre contenu :
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-1">
                <code className="bg-white px-2 py-1 rounded text-cyan-600">&lt;h2&gt;Titre&lt;/h2&gt;</code>
                <code className="bg-white px-2 py-1 rounded text-cyan-600">&lt;h3&gt;Sous-titre&lt;/h3&gt;</code>
                <code className="bg-white px-2 py-1 rounded text-cyan-600">&lt;p&gt;Paragraphe&lt;/p&gt;</code>
              </div>
              <div className="space-y-1">
                <code className="bg-white px-2 py-1 rounded text-cyan-600">&lt;strong&gt;Gras&lt;/strong&gt;</code>
                <code className="bg-white px-2 py-1 rounded text-cyan-600">&lt;ul&gt;&lt;li&gt;Liste&lt;/li&gt;&lt;/ul&gt;</code>
                <code className="bg-white px-2 py-1 rounded text-cyan-600">&lt;a href=""&gt;Lien&lt;/a&gt;</code>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CMSPagesPage;
